import { Alert, Button, Input, Modal, Select, Spin } from "@arco-design/web-react";
import { BedDouble, MapPin, Moon, Plane, School, Sun, Sunrise, TrainFront, type LucideIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { isHttpClientError } from "../../../lib/http/errors";
import type {
  CandidateKind,
  ItineraryNode,
  NodeDraft,
  PointCandidate,
  PointCandidateResult,
  PointCandidateQuery,
  PointRecommendationAnchor,
  RailwayPairDraft,
  TimeSlot,
  MajorCandidate,
  RailwayQuery,
  RailwaySearchResult,
} from "../types";
import { ItineraryRailwayPicker } from "./ItineraryRailwayPicker";
import type { RailwayInsertionDraft } from "../service/itineraryRailwayInsertionService";
import { ItineraryDialogCloseButton } from "./ItineraryDialogCloseButton";

export type PointNodeCandidateKind = Exclude<CandidateKind, "transport_station">;
type RailwayPairSubmit = {
  pair: RailwayPairDraft;
  timeSlot: TimeSlot;
  majorId?: string | null;
};

type Props = {
  visible: boolean;
  title: string;
  initial?: ItineraryNode | null;
  defaultSlot: TimeSlot;
  lockSlot?: boolean;
  selectionOnly?: boolean;
  candidateLabel?: string;
  onCancel: () => void;
  onSubmit: (draft: NodeDraft & { timeSlot: TimeSlot; majorId?: string | null }) => void;
  /** 多专业行程的专业候选；为空时保持普通编辑体验。 */
  majorOptions?: readonly Pick<MajorCandidate, "id" | "name">[];
  defaultMajorId?: string | null;
  lockMajor?: boolean;
  listCandidates?: (query: PointCandidateQuery, signal?: AbortSignal) => Promise<PointCandidateResult>;
  getRecommendationAnchor?: (timeSlot: TimeSlot, majorId: string | null, kind: PointNodeCandidateKind) => PointRecommendationAnchor | null;
  enableRailwayPair?: boolean;
  enableRailwaySearch?: boolean;
  railwaySearchOnAdd?: boolean;
  travelDate?: string | null;
  getRailwayOptions?: (query: RailwayQuery, signal?: AbortSignal) => Promise<RailwaySearchResult>;
  onRailwaySubmit?: (draft: RailwayInsertionDraft) => void;
  onRailwayPairSubmit?: (draft: RailwayPairSubmit) => void;
  getAllowedKinds?: (timeSlot: TimeSlot, majorId?: string | null) => readonly PointNodeCandidateKind[];
  submitError?: string;
  onDraftStateChange?: (dirty: boolean) => void;
};

const candidateKinds: ReadonlyArray<{ value: PointNodeCandidateKind; label: string; icon: LucideIcon }> = [
  { value: "point", label: "考察点", icon: MapPin },
  { value: "stay_area", label: "住宿点", icon: BedDouble },
  { value: "railway_station", label: "火车站", icon: TrainFront },
  { value: "airport", label: "机场", icon: Plane },
  { value: "organization_site", label: "学校校区", icon: School },
];

const railwayQueryKind = { value: "railway_query" as const, label: "查火车", icon: TrainFront };

const railwayPairKind = { value: "railway_pair" as const, label: "大区域火车", icon: TrainFront };

const timeSlots: ReadonlyArray<{ value: TimeSlot; label: string; icon: LucideIcon }> = [
  { value: "morning", label: "上午", icon: Sunrise },
  { value: "afternoon", label: "下午", icon: Sun },
  { value: "evening", label: "晚上", icon: Moon },
];

/** 新增与编辑行程点位的统一弹窗。 */
export function ItineraryPointDialog({
  visible,
  title,
  initial,
  defaultSlot,
  lockSlot = false,
  selectionOnly = false,
  candidateLabel = "候选点位",
  onCancel,
  onSubmit,
  majorOptions = [],
  defaultMajorId = null,
  lockMajor = false,
  listCandidates,
  getRecommendationAnchor,
  enableRailwayPair = false,
  enableRailwaySearch = false,
  railwaySearchOnAdd = false,
  travelDate,
  getRailwayOptions,
  onRailwaySubmit,
  onRailwayPairSubmit,
  getAllowedKinds,
  submitError,
  onDraftStateChange,
}: Props) {
  const [description, setDescription] = useState("");
  const [majorId, setMajorId] = useState<string | null>(defaultMajorId);
  const [timeSlot, setTimeSlot] = useState<TimeSlot>(defaultSlot);
  const [candidateKind, setCandidateKind] = useState<PointNodeCandidateKind>("point");
  const [entryMode, setEntryMode] = useState<"single" | "railway_pair" | "railway_query">("single");
  const [keyword, setKeyword] = useState("");
  const [candidates, setCandidates] = useState<PointCandidate[]>([]);
  const [selected, setSelected] = useState<PointCandidate | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [error, setError] = useState("");
  const [originSelected, setOriginSelected] = useState<PointCandidate | null>(null);
  const [destinationSelected, setDestinationSelected] = useState<PointCandidate | null>(null);
  const generationRef = useRef(0);
  const searchControllerRef = useRef<AbortController | null>(null);
  const searchTimerRef = useRef<number | undefined>(undefined);
  const contextRef = useRef("");
  const initializedContextRef = useRef("");
  const initialKey = initial
    ? [initial.id, initial.title, initial.nodeType, initial.pointSpuId, initial.stayAreaId, initial.organizationSiteId].join(":")
    : "new";
  const recommendationAnchor = visible && entryMode === "single" && (candidateKind === "point" || candidateKind === "stay_area")
    ? getRecommendationAnchor?.(timeSlot, majorId, candidateKind) ?? null : null;
  const recommendationKey = `${recommendationAnchor?.refType ?? ""}:${recommendationAnchor?.refId ?? ""}:${timeSlot}:${majorId ?? ""}`;
  const slotLocked = lockSlot && entryMode !== "railway_query";
  const displayedKinds = enableRailwaySearch
    ? [...candidateKinds.slice(0, 2), railwayQueryKind, ...candidateKinds.slice(railwaySearchOnAdd ? 2 : 3)]
    : [...candidateKinds.slice(0, 2), ...(enableRailwayPair ? [railwayPairKind] : []), ...candidateKinds.slice(2)];
  const allowedKinds = getAllowedKinds?.(timeSlot, majorId) ?? candidateKinds.map((item) => item.value);
  const selectedValid = selected !== null && isValidCandidate(selected) && allowedKinds.includes(kindFromCandidate(selected));
  const railwayPairValid = Boolean(
    enableRailwayPair
    && onRailwayPairSubmit
    && allowedKinds.includes("railway_station")
    && originSelected
    && destinationSelected
    && isValidRailwayRegionCandidate(originSelected)
    && isValidRailwayRegionCandidate(destinationSelected)
    && normalizeRegionName(originSelected.railwayRegionName) !== normalizeRegionName(destinationSelected.railwayRegionName),
  );
  const visibleCandidates = selected
    && (!(candidateKind === "point" || candidateKind === "stay_area")
      || !candidates.some((candidate) => candidateKey(candidate) === candidateKey(selected)))
    ? [...new Map([selected, ...candidates].map((candidate) => [candidateKey(candidate), candidate])).values()]
    : candidates;
  contextRef.current = `${visible ? "open" : "closed"}|${initialKey}|${candidateKind}|${keyword}|${recommendationKey}`;

  useEffect(() => {
    const initializationContext = JSON.stringify([visible, initialKey, defaultSlot, defaultMajorId]);
    // 后台回填会替换位置规则回调；同一编辑会话不得因此重置尚未提交的输入。
    if (initializedContextRef.current === initializationContext) return;
    initializedContextRef.current = initializationContext;
    generationRef.current += 1;
    setLoading(false);
    setHasQueried(false);
    setCandidates([]);
    setError("");
    setKeyword("");
    onDraftStateChange?.(false);
    if (!visible) return;
    const slot = initial?.timeSlot ?? defaultSlot;
    const nextAllowedKinds = getAllowedKinds?.(slot, defaultMajorId) ?? candidateKinds.map((item) => item.value);
    const initialCandidate = candidateFromNode(initial);
    const initialKind = initialCandidate ? kindFromCandidate(initialCandidate) : "point";
    setTimeSlot(slot);
    setCandidateKind(nextAllowedKinds.includes(initialKind) ? initialKind : nextAllowedKinds[0] ?? "point");
    setSelected(initialCandidate && nextAllowedKinds.includes(initialKind) ? initialCandidate : null);
    setEntryMode("single");
    setOriginSelected(null);
    setDestinationSelected(null);
    setDescription(initial?.description ?? "");
    setMajorId(defaultMajorId);
  }, [defaultMajorId, defaultSlot, getAllowedKinds, initialKey, onDraftStateChange, visible]);

  const clearCandidates = () => {
    searchControllerRef.current?.abort();
    window.clearTimeout(searchTimerRef.current);
    generationRef.current += 1;
    setCandidates([]);
    setSelected(null);
    setLoading(false);
    setHasQueried(false);
    setError("");
  };

  const clearRailwayPair = () => {
    setOriginSelected(null);
    setDestinationSelected(null);
  };

  const cancel = () => {
    searchControllerRef.current?.abort();
    window.clearTimeout(searchTimerRef.current);
    generationRef.current += 1;
    setLoading(false);
    onCancel();
  };

  const search = async (searchKeyword = keyword, searchKind = candidateKind, recommend = false) => {
    window.clearTimeout(searchTimerRef.current);
    searchControllerRef.current?.abort();
    if (!listCandidates) return;
    const normalizedKeyword = searchKeyword.trim();
    const anchor = recommend && !normalizedKeyword && (searchKind === "point" || searchKind === "stay_area")
      ? recommendationAnchor : null;
    if (!normalizedKeyword && !anchor) {
      setLoading(false);
      setHasQueried(false);
      setError("");
      return;
    }
    const generation = generationRef.current + 1;
    const controller = new AbortController();
    searchControllerRef.current = controller;
    generationRef.current = generation;
    const requestKey = `open|${initialKey}|${searchKind}|${searchKeyword}|${recommendationKey}`;
    setLoading(true);
    setHasQueried(false);
    setError("");
    try {
      const result = await listCandidates({
        kind: searchKind,
        ...(anchor ? { recommendation: true, anchorRefType: anchor.refType, anchorRefId: anchor.refId }
          : { keyword: normalizedKeyword }),
        ...(searchKind === "stay_area" ? { includeTrainOvernight: true } : {}),
        ...(searchKind !== "point" && searchKind !== "stay_area" ? { page: 1, pageSize: 20 } : {}),
      }, controller.signal);
      if (!controller.signal.aborted && generation === generationRef.current && requestKey === contextRef.current) {
        setCandidates(result.items.filter((candidate) => kindFromCandidate(candidate) === searchKind));
        setHasQueried(true);
      }
    } catch (reason) {
      if (!controller.signal.aborted && generation === generationRef.current && requestKey === contextRef.current) {
        setError(pointCandidateErrorMessage(reason));
      }
    } finally {
      if (!controller.signal.aborted && generation === generationRef.current && requestKey === contextRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // 草稿位置变化只使候选失效，不清空用户已选资源或未提交的说明。
    searchControllerRef.current?.abort();
    generationRef.current += 1;
    setCandidates([]);
    setLoading(false);
    setHasQueried(false);
    setError("");
    return () => searchControllerRef.current?.abort();
  }, [recommendationKey, visible, entryMode]);

  useEffect(() => {
    if (!visible || entryMode !== "single" || !listCandidates) return;
    if (!keyword.trim()) {
      setLoading(false);
      setHasQueried(false);
      return;
    }
    const timer = window.setTimeout(
      () => void search(keyword, candidateKind),
      300,
    );
    searchTimerRef.current = timer;
    return () => {
      window.clearTimeout(timer);
      searchControllerRef.current?.abort();
    };
  }, [candidateKind, entryMode, initialKey, keyword, listCandidates, recommendationKey, visible]);

  const changeSlot = (nextSlot: TimeSlot) => {
    if (slotLocked) return;
    onDraftStateChange?.(true);
    const nextAllowedKinds = getAllowedKinds?.(nextSlot, majorId) ?? candidateKinds.map((item) => item.value);
    setTimeSlot(nextSlot);
    if (!nextAllowedKinds.includes(candidateKind)) {
      clearCandidates();
      setCandidateKind(nextAllowedKinds[0] ?? "point");
    }
  };

  const submit = () => {
    if (entryMode === "railway_pair") {
      if (!railwayPairValid || !originSelected || !destinationSelected || !onRailwayPairSubmit) return;
      onRailwayPairSubmit({
        pair: {
          origin: railwayNodeDraft(originSelected),
          destination: railwayNodeDraft(destinationSelected),
        },
        timeSlot,
        ...(majorOptions.length ? { majorId } : {}),
      });
      return;
    }
    if (!selectedValid || !selected) return;
    const nodeType = selected.refType === "point_spu"
      ? candidateKind
      : selected.refType === "stay_area"
        ? "stay_area"
        : "organization_site";
    const originalCandidate = candidateFromNode(initial);
    const sameResource = initial?.nodeType === nodeType && originalCandidate !== null
      && candidateKey(originalCandidate) === candidateKey(selected);
    onSubmit({
      title: selected.name.trim(),
      // 同资源编辑不回放不可见字段，保留最新草稿；实际换资源时才清理旧元数据。
      ...(nodeType === "point" ? { description: description.trim() || null } : sameResource ? {} : { description: null }),
      ...(!sameResource ? { imageUrl: selected.headImageUrl ?? null, railwayRegionName: selected.railwayRegionName ?? null } : {}),
      nodeType,
      timeSlot,
      pointSpuId: selected.refType === "point_spu" ? selected.refId : null,
      stayAreaId: selected.refType === "stay_area" ? selected.refId : null,
      organizationSiteId: selected.refType === "organization_site" ? Number(selected.refId) : null,
      ...(majorOptions.length ? { majorId } : {}),
    });
  };

  return (
    <Modal
      className={`itinerary-point-modal${enableRailwayPair ? " has-railway-pair" : ""}${entryMode === "railway_query" ? " has-railway-query" : ""}`}
      footer={entryMode === "railway_query" ? null : undefined}
      visible={visible}
      title={title}
      closeIcon={<ItineraryDialogCloseButton label={`关闭${title}`} />}
      okText={entryMode === "railway_pair" ? "保存火车区间" : "保存点位"}
      cancelText="取消"
      okButtonProps={{ disabled: entryMode === "railway_pair" ? !railwayPairValid : !selectedValid }}
      onCancel={cancel}
      onOk={submit}
    >
      <div className="itinerary-dialog-form">
        {submitError ? (
          <Alert className="itinerary-dialog-submit-error" type="error" content={submitError} />
        ) : null}
        {listCandidates ? (
          <section className="itinerary-point-candidates" aria-label="MySQL 点位候选">
            {!selectionOnly ? <>
              <span className="itinerary-candidate-kind-label" id="itinerary-candidate-kind-label">候选类型</span>
              <div
                aria-labelledby="itinerary-candidate-kind-label"
                className={`itinerary-candidate-kind-options${displayedKinds.length > 5 ? " has-railway-pair" : ""}`}
                role="group"
              >
                {displayedKinds.map((item) => {
                  const isRailwayQuery = item.value === "railway_query";
                  const isRailwayPair = item.value === "railway_pair";
                  const isKindAllowed = isRailwayQuery
                    ? Boolean(getRailwayOptions && onRailwaySubmit) && (railwaySearchOnAdd || allowedKinds.includes("railway_station"))
                    : isRailwayPair
                    ? enableRailwayPair && Boolean(onRailwayPairSubmit) && allowedKinds.includes("railway_station")
                    : allowedKinds.includes(item.value as PointNodeCandidateKind);
                  return (
                    <button
                      aria-disabled={!isKindAllowed}
                      aria-pressed={isRailwayQuery ? entryMode === "railway_query" : isRailwayPair ? entryMode === "railway_pair" : entryMode === "single" && candidateKind === item.value}
                      className={`itinerary-candidate-kind-card itinerary-candidate-kind-card--${item.value}${(isRailwayQuery ? entryMode === "railway_query" : isRailwayPair ? entryMode === "railway_pair" : entryMode === "single" && candidateKind === item.value) ? " is-selected" : ""}${isKindAllowed ? "" : " is-disabled"}`}
                      key={item.value}
                      title={!isKindAllowed
                        ? item.value === "organization_site" ? "学校校区只能放在当天首尾" : "当前位置不允许选择此类型"
                        : undefined}
                      disabled={!isKindAllowed}
                      type="button"
                      onClick={() => {
                        if (isRailwayQuery) {
                          if (entryMode === "railway_query" || !isKindAllowed) return;
                          onDraftStateChange?.(true);
                          clearCandidates(); clearRailwayPair(); setKeyword("");
                          setEntryMode("railway_query");
                          return;
                        }
                        if ((isRailwayPair && entryMode === "railway_pair")
                          || (!isRailwayPair && entryMode === "single" && candidateKind === item.value)) return;
                        if (isRailwayPair) {
                          onDraftStateChange?.(true);
                          if (!isKindAllowed) return;
                          clearCandidates();
                          setKeyword("");
                          setEntryMode("railway_pair");
                          clearRailwayPair();
                          return;
                        }
                        if (!isKindAllowed) return;
                        onDraftStateChange?.(true);
                        setEntryMode("single");
                        if (lockSlot) setTimeSlot(initial?.timeSlot ?? defaultSlot);
                        clearRailwayPair();
                        clearCandidates();
                        setKeyword("");
                        setDescription("");
                        setCandidateKind(item.value as PointNodeCandidateKind);
                      }}
                    >
                      <span className="itinerary-candidate-kind-card-content">
                        <item.icon size={18} strokeWidth={1.8} aria-hidden="true" />
                        <span>{item.label}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </> : null}
            {entryMode === "single" ? <>
              <label htmlFor="itinerary-candidate-keyword">{candidateLabel}</label>
              <Select
              id="itinerary-candidate-keyword"
              aria-label={candidateLabel}
              showSearch
              allowClear
              filterOption={false}
              loading={loading}
              value={selected ? candidateKey(selected) : undefined}
              inputValue={keyword}
              placeholder={selectionOnly && initial?.title.trim() === "无" ? "无" : candidateKind === "point" || candidateKind === "stay_area"
                ? "输入名称搜索" : "输入名称搜索，最多展示 20 条"}
              notFoundContent={null}
              onVisibleChange={(open) => { if (open && !keyword.trim()) void search(keyword, candidateKind, true); }}
              options={visibleCandidates.map((candidate) => ({
                value: candidateKey(candidate),
                label: (
                  <span style={{ display: "grid", gap: 2, paddingBlock: 6, lineHeight: 1.4 }}>
                    <span>{candidate.name}</span>
                    {!(candidateKind === "stay_area" && candidate.name.trim() === "无")
                      && (candidate.city?.trim() || candidate.address?.trim()) ? (
                      <small style={{ display: "block", opacity: 0.72 }}>
                        {[candidate.city?.trim(), candidate.address?.trim()].filter(Boolean).join(" · ")}
                      </small>
                    ) : null}
                  </span>
                ),
                extra: candidate,
              }))}
              renderFormat={(option) => (option?.extra as PointCandidate | undefined)?.name ?? option?.label ?? ""}
              onClear={() => { clearCandidates(); setKeyword(""); }}
              onInputValueChange={(nextKeyword, reason) => {
                if (reason !== "manual") return;
                const boundedKeyword = Array.from(nextKeyword).slice(0, 300).join("");
                if (boundedKeyword === keyword) return;
                searchControllerRef.current?.abort();
                setKeyword(boundedKeyword);
                onDraftStateChange?.(true);
                generationRef.current += 1;
                setCandidates([]);
                setSelected(null);
                setDescription("");
                setLoading(Boolean(boundedKeyword.trim()));
                setHasQueried(false);
                setError("");
              }}
              onChange={(value) => {
                const next = visibleCandidates.find((candidate) => candidateKey(candidate) === value) ?? null;
                if (next && selected && candidateKey(next) === candidateKey(selected)) return;
                searchControllerRef.current?.abort();
                window.clearTimeout(searchTimerRef.current);
                generationRef.current += 1;
                setLoading(false);
                onDraftStateChange?.(true);
                setSelected(next);
                setDescription("");
              }}
              />
              {loading ? <div><Spin size={16} /> 正在搜索</div> : null}
              {error ? <div role="alert"><Alert type="error" content={error} /><Button onClick={() => void search(keyword, candidateKind, !keyword.trim())}>重试</Button></div> : null}
              {hasQueried && !loading && !error && candidates.length === 0 ? <p>暂无候选，请调整关键词后重试</p> : null}
            </> : visible && entryMode === "railway_pair" ? <div className="itinerary-railway-pair-fields">
              <RailwayRegionField label="起点大区域" id="itinerary-railway-origin" value={originSelected} listCandidates={listCandidates}
                onChange={(value) => { onDraftStateChange?.(true); setOriginSelected(value); }} />
              <RailwayRegionField label="终点大区域" id="itinerary-railway-destination" value={destinationSelected} listCandidates={listCandidates}
                onChange={(value) => { onDraftStateChange?.(true); setDestinationSelected(value); }} />
              {originSelected && destinationSelected && normalizeRegionName(originSelected.railwayRegionName) === normalizeRegionName(destinationSelected.railwayRegionName)
                ? <div className="itinerary-railway-pair-error" role="alert"><Alert type="warning" content="起点和终点不能选择同一个大区域" /></div>
                : null}
            </div> : null}
          </section>
        ) : null}
        {!selectionOnly ? <>
          {majorOptions.length ? (
            <>
              <label htmlFor="itinerary-point-major">所属专业</label>
              <select
                id="itinerary-point-major"
                value={majorId ?? ""}
                disabled={lockMajor}
                onChange={(event) => {
                  onDraftStateChange?.(true);
                  setMajorId(event.target.value || null);
                }}
              >
                {!majorOptions.some((major) => !major.id) ? <option value="" disabled>请选择专业</option> : null}
                {majorOptions.map((major) => <option key={major.id} value={major.id}>{major.name}</option>)}
              </select>
            </>
          ) : null}
          <span className="itinerary-point-slot-label" id="itinerary-point-slot-label">所属时段</span>
          <div
            aria-describedby={slotLocked ? "itinerary-point-slot-hint" : undefined}
            aria-labelledby="itinerary-point-slot-label"
            className="itinerary-time-slot-options"
            role="group"
          >
            {timeSlots.map((slot) => (
              <button
                aria-pressed={timeSlot === slot.value}
                className={`itinerary-time-slot-card${timeSlot === slot.value ? " is-selected" : ""}`}
                disabled={slotLocked}
                key={slot.value}
                type="button"
                onClick={() => changeSlot(slot.value)}
              >
                <slot.icon size={14} strokeWidth={1.8} aria-hidden="true" />
                <span>{slot.label}</span>
              </button>
            ))}
          </div>
          {slotLocked ? (
            <p className="itinerary-point-slot-hint" id="itinerary-point-slot-hint">
              前插或后插将沿用目标点位的所属时段。
            </p>
          ) : null}
          {entryMode === "single" && candidateKind === "point" ? <div className="itinerary-point-description-field">
            <label htmlFor="itinerary-point-description">
              点位说明
            </label>
            <Input.TextArea
              id="itinerary-point-description"
              value={description}
              maxLength={1000}
              autoSize={{ minRows: 3, maxRows: 6 }}
              onChange={(value) => {
                onDraftStateChange?.(true);
                setDescription(value);
              }}
            />
          </div> : null}
        </> : null}
      </div>
      {visible && entryMode === "railway_query" && getRailwayOptions && onRailwaySubmit ? (
        <ItineraryRailwayPicker
          key={`${initialKey}|${defaultSlot}|${defaultMajorId}`}
          travelDate={travelDate}
          timeSlot={timeSlot}
          majorId={majorOptions.length ? majorId : undefined}
          disabled={!railwaySearchOnAdd && !allowedKinds.includes("railway_station")}
          getRailwayOptions={getRailwayOptions}
          listCandidates={listCandidates}
          onCancel={cancel}
          onSubmit={onRailwaySubmit}
          onDraftStateChange={onDraftStateChange}
        />
      ) : null}
    </Modal>
  );
}

/** 两端独立搜索，只接受主数据确认的区域，并隔离输入变化和关闭后的迟到响应。 */
function RailwayRegionField({ label, id, value, listCandidates, onChange }: {
  label: string;
  id: string;
  value: PointCandidate | null;
  listCandidates: NonNullable<Props["listCandidates"]>;
  onChange: (value: PointCandidate | null) => void;
}) {
  const [keyword, setKeyword] = useState("");
  const [candidates, setCandidates] = useState<PointCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const current = ++generation.current;
    setCandidates([]);
    setError("");
    setQueried(false);
    setLoading(Boolean(keyword.trim()));
    const timer = window.setTimeout(async () => {
      if (!keyword.trim()) return;
      try {
        const result = await listCandidates({ kind: "railway_station", railwayScope: "region", keyword: keyword.trim(), page: 1, pageSize: 20 });
        if (current !== generation.current) return;
        setCandidates(result.items.filter(isValidRailwayRegionCandidate));
        setQueried(true);
      } catch (reason) {
        if (current === generation.current) setError(pointCandidateErrorMessage(reason));
      } finally {
        if (current === generation.current) setLoading(false);
      }
    }, 300);
    return () => { generation.current += 1; window.clearTimeout(timer); };
  }, [keyword, listCandidates, retry]);

  const options = value ? [value, ...candidates.filter((item) => candidateKey(item) !== candidateKey(value))] : candidates;
  return <>
    <label htmlFor={id}>{label}</label>
    <Select id={id} aria-label={label} showSearch allowClear filterOption={false}
      value={value ? candidateKey(value) : undefined} inputValue={keyword} loading={loading}
      placeholder="输入区域名称搜索，最多展示 20 条" notFoundContent={null}
      options={options.map((item) => ({ value: candidateKey(item), label: item.railwayRegionName?.trim() || item.name }))}
      onInputValueChange={(input, reason) => {
        if (reason !== "manual") return;
        generation.current += 1;
        setKeyword(Array.from(input).slice(0, 300).join(""));
        setCandidates([]);
        setError("");
        setQueried(false);
        onChange(null);
      }}
      onChange={(key) => {
        generation.current += 1;
        onChange(options.find((item) => candidateKey(item) === key) ?? null);
        setKeyword("");
        setLoading(false);
      }} />
    {error ? <div className="itinerary-railway-pair-error" role="alert"><Alert type="error" content={`${label}：${error}`} /><Button onClick={() => setRetry((value) => value + 1)}>重试{label}</Button></div> : null}
    {queried && !loading && !error && !candidates.length ? <p className="itinerary-railway-pair-error">{label}暂无匹配候选，请调整关键词后重试</p> : null}
  </>;
}

/** 将远程候选错误转换为用户可执行的稳定提示。 */
function pointCandidateErrorMessage(reason: unknown): string {
  if (!isHttpClientError(reason)) return "点位候选加载失败，请稍后重试。";
  if (reason.statusCode === null || reason.code === "ERR_NETWORK") {
    return "点位候选加载失败，请检查网络后重试。";
  }
  if (reason.statusCode === 400 || reason.statusCode === 422) {
    return "点位搜索条件无效，请调整关键词后重试。";
  }
  if (reason.retriable || reason.statusCode >= 500) {
    return "点位候选服务暂时不可用，请稍后重试。";
  }
  return "点位候选加载失败，请稍后重试。";
}

/** 从已有节点恢复可继续保存的主数据候选。 */
function candidateFromNode(node?: ItineraryNode | null): PointCandidate | null {
  if (!node?.title.trim()) return null;
  if (node.pointSpuId) return {
    refType: "point_spu",
    refId: node.pointSpuId,
    railwayRegionName: node.railwayRegionName,
    name: node.title,
    pointType: node.nodeType === "railway_station" ? "火车站" : node.nodeType === "airport" ? "机场" : undefined,
  };
  if (node.stayAreaId) return { refType: "stay_area", refId: node.stayAreaId, name: node.title };
  if (node.organizationSiteId != null) {
    return { refType: "organization_site", refId: String(node.organizationSiteId), name: node.title };
  }
  return null;
}

/** 把候选引用映射为点位编辑可见类型。 */
function kindFromCandidate(candidate: PointCandidate): PointNodeCandidateKind {
  if (candidate.refType === "point_spu") {
    if (candidate.pointType === "火车站" || candidate.pointType === "车站") return "railway_station";
    if (candidate.pointType === "机场") return "airport";
    return "point";
  }
  return candidate.refType;
}

/** 为远程下拉构造稳定且跨资源类型不冲突的值。 */
function candidateKey(candidate: PointCandidate): string {
  return `${candidate.refType}:${candidate.refId}`;
}

/** 统一比较主数据区域名，拒绝同一区域的不同代表资源。 */
function normalizeRegionName(value?: string | null): string {
  return value?.normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase() ?? "";
}

/** 区域身份以服务器元数据为准，不能由具体车站的展示名推断。 */
function isValidRailwayRegionCandidate(candidate: PointCandidate): boolean {
  return candidate.refType === "point_spu"
    && candidate.pointType === "火车站"
    && Boolean(candidate.refId.trim())
    && Boolean(candidate.name.trim())
    && Boolean(candidate.railwayRegionName?.trim());
}

/** 把区域候选投影为现有铁路节点，不创造第二套节点类型。 */
function railwayNodeDraft(candidate: PointCandidate): NodeDraft {
  return {
    title: candidate.railwayRegionName?.trim() || candidate.name.trim(),
    nodeType: "railway_station",
    pointSpuId: candidate.refId,
    railwayRegionName: candidate.railwayRegionName?.trim() || candidate.name.trim(),
    stayAreaId: null,
    organizationSiteId: null,
    description: null,
    imageUrl: candidate.headImageUrl ?? null,
    planStartAt: null,
    planEndAt: null,
  };
}

/** 拒绝空名称、空引用和不可落库的校区 ID。 */
function isValidCandidate(candidate: PointCandidate): boolean {
  if (!candidate.name.trim() || !candidate.refId.trim()) return false;
  return candidate.refType !== "organization_site" || Number.isFinite(Number(candidate.refId));
}
